import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Grid, 
  Paper,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  FormGroup,
  Slider,
  Button,
  Divider,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaletteIcon from '@mui/icons-material/Palette';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SecurityIcon from '@mui/icons-material/Security';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import SaveIcon from '@mui/icons-material/Save';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GroupIcon from '@mui/icons-material/Group';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import UserCreateModal from './shared/UserCreateModal';
import ScheduleManagement from './physician/ScheduleManagement';

// Animation
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled components
const SettingsContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(4, 0),
  animation: `${fadeIn} 0.5s ease-out`,
}));

const SettingsHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  display: 'flex',
  alignItems: 'center',
}));

const SettingsPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const Settings = () => {
  const { profile, isAdmin, isProvider } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const isPhysician = profile?.tenant_role === 'physician' || isProvider;
  const isClinicAdmin = profile?.tenant_role === 'clinic_admin' || isAdmin;
  const userRole = profile?.tenant_role || '';
  const [clinicStaffModalOpen, setClinicStaffModalOpen] = useState(false);
  
  // Appearance settings
  const [appearanceSettings, setAppearanceSettings] = useState({
    theme: 'light',
    fontSize: 16,
    highContrast: false,
  });
  
  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    email: true,
    sms: true,
    push: true,
    appointments: true,
    messages: true,
    prescriptions: true,
    labResults: true,
  });
  
  // Privacy & Security settings
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    dataSharing: 'minimal',
    sessionTimeout: 30,
  });
  
  // Accessibility settings
  const [accessibilitySettings, setAccessibilitySettings] = useState({
    screenReader: false,
    keyboardNavigation: true,
    reduceMotion: false,
    autoplayVideos: true,
  });
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Handle appearance settings change
  const handleAppearanceChange = (setting, value) => {
    setAppearanceSettings(prev => ({ ...prev, [setting]: value }));
  };
  
  // Handle notification settings change
  const handleNotificationChange = (setting) => {
    setNotificationSettings(prev => ({ ...prev, [setting]: !prev[setting] }));
  };
  
  // Handle security settings change
  const handleSecurityChange = (setting, value) => {
    setSecuritySettings(prev => ({ ...prev, [setting]: value }));
  };
  
  // Handle accessibility settings change
  const handleAccessibilityChange = (setting) => {
    setAccessibilitySettings(prev => ({ ...prev, [setting]: !prev[setting] }));
  };
  
  // Admin settings
  const [adminSettings, setAdminSettings] = useState({
    enableAdminMode: false,
    allowNewPhysicianRegistration: true,
    requireApprovalForNewPhysicians: true,
  });
  
  // Handle admin settings change
  const handleAdminChange = (setting, value) => {
    setAdminSettings(prev => ({ ...prev, [setting]: value }));
  };
  
  // Toggle admin status for the current physician
  const toggleAdminStatus = () => {
    // This functionality requires backend API implementation
    // Admin status should be updated in the database via roleManagementService
    if (import.meta.env.DEV) console.log('Admin role toggle requires backend API - Phase 2 implementation');
    // TODO: Implement role promotion API call
  };
  
  // Handle save settings
  const handleSaveSettings = () => {
    if (import.meta.env.DEV) console.log('Saving settings:', {
      appearance: appearanceSettings,
      notifications: notificationSettings,
      security: securitySettings,
      accessibility: accessibilitySettings,
      admin: adminSettings,
    });
    // Here you would typically make an API call to save the settings
  };
  
  // Determine back link based on user role
  const getBackLink = () => {
    if (userRole === 'physician') {
      return '/physician/profile';
    } else if (userRole === 'sponsor') {
      return '/sponsor/profile';
    } else {
      return '/dashboard';
    }
  };
  
  return (
    <SettingsContainer>
      <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100%' }}>
        <SettingsHeader>
          <Button 
            component={Link} 
            to={getBackLink()} 
            startIcon={<ArrowBackIcon />}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Settings
          </Typography>
        </SettingsHeader>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Paper elevation={1} sx={{ borderRadius: 2 }}>
              <Tabs
                orientation="vertical"
                value={activeTab}
                onChange={handleTabChange}
                aria-label="settings tabs"
                sx={{ borderRight: 1, borderColor: 'divider' }}
              >
                <Tab 
                  icon={<PaletteIcon />} 
                  label="Appearance" 
                  id="settings-tab-0"
                  aria-controls="settings-tabpanel-0"
                  sx={{ minHeight: 64, alignItems: 'flex-start', pl: 3 }}
                />
                <Tab 
                  icon={<NotificationsIcon />} 
                  label="Notifications" 
                  id="settings-tab-1"
                  aria-controls="settings-tabpanel-1"
                  sx={{ minHeight: 64, alignItems: 'flex-start', pl: 3 }}
                />
                <Tab 
                  icon={<SecurityIcon />} 
                  label="Privacy & Security" 
                  id="settings-tab-2"
                  aria-controls="settings-tabpanel-2"
                  sx={{ minHeight: 64, alignItems: 'flex-start', pl: 3 }}
                />
                <Tab 
                  icon={<AccessibilityIcon />} 
                  label="Accessibility" 
                  id="settings-tab-3"
                  aria-controls="settings-tabpanel-3"
                  sx={{ minHeight: 64, alignItems: 'flex-start', pl: 3 }}
                />
                {isPhysician && (
                  <Tab 
                    icon={<CalendarMonthIcon />} 
                    label="Schedule" 
                    id="settings-tab-schedule"
                    aria-controls="settings-tabpanel-schedule"
                    sx={{ minHeight: 64, alignItems: 'flex-start', pl: 3 }}
                  />
                )}
                {isPhysician && (
                  <Tab 
                    icon={<AdminPanelSettingsIcon />} 
                    label="Admin Settings" 
                    id="settings-tab-4"
                    aria-controls="settings-tabpanel-4"
                    sx={{ minHeight: 64, alignItems: 'flex-start', pl: 3 }}
                  />
                )}
                {(isPhysician || isClinicAdmin) && (
                  <Tab 
                    icon={<GroupIcon />} 
                    label="User Management" 
                    id="settings-tab-5"
                    aria-controls="settings-tabpanel-5"
                    sx={{ minHeight: 64, alignItems: 'flex-start', pl: 3 }}
                  />
                )}
              </Tabs>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={9}>
            <Paper 
              elevation={1} 
              sx={{ 
                borderRadius: 2,
                minHeight: 500,
              }}
            >
              {/* Appearance Settings */}
              <SettingsPanel
                role="tabpanel"
                hidden={activeTab !== 0}
                id="settings-tabpanel-0"
                aria-labelledby="settings-tab-0"
              >
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  Appearance Settings
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Theme
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={appearanceSettings.theme}
                        onChange={(e) => handleAppearanceChange('theme', e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="light">Light</MenuItem>
                        <MenuItem value="dark">Dark</MenuItem>
                        <MenuItem value="system">System Default</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mr: 2 }}>
                        Font Size
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Current: {appearanceSettings.fontSize}px
                      </Typography>
                    </Box>
                    
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        border: '1px dashed', 
                        borderColor: 'divider',
                        borderRadius: 2,
                        mb: 2
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <span role="img" aria-label="tip" style={{ marginRight: '8px' }}>💡</span>
                        Drag the slider handle to adjust font size
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ mr: 2, minWidth: '50px' }}>
                          Small
                        </Typography>
                        <Slider
                          value={appearanceSettings.fontSize}
                          min={12}
                          max={24}
                          step={1}
                          onChange={(e, value) => handleAppearanceChange('fontSize', value)}
                          valueLabelDisplay="on"
                          aria-label="Font size slider"
                          sx={{ mx: 1 }}
                        />
                        <Typography variant="body2" sx={{ ml: 2, minWidth: '50px' }}>
                          Large
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={appearanceSettings.highContrast}
                          onChange={() => handleAppearanceChange('highContrast', !appearanceSettings.highContrast)}
                        />
                      }
                      label="High Contrast Mode"
                    />
                  </Grid>
                </Grid>
              </SettingsPanel>
              
              {/* Notification Settings */}
              <SettingsPanel
                role="tabpanel"
                hidden={activeTab !== 1}
                id="settings-tabpanel-1"
                aria-labelledby="settings-tab-1"
              >
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  Notification Settings
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Notification Methods
                    </Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={notificationSettings.email}
                            onChange={() => handleNotificationChange('email')}
                          />
                        }
                        label="Email Notifications"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={notificationSettings.sms}
                            onChange={() => handleNotificationChange('sms')}
                          />
                        }
                        label="SMS Notifications"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={notificationSettings.push}
                            onChange={() => handleNotificationChange('push')}
                          />
                        }
                        label="Push Notifications"
                      />
                    </FormGroup>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Notification Types
                    </Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={notificationSettings.appointments}
                            onChange={() => handleNotificationChange('appointments')}
                          />
                        }
                        label="Appointment Reminders"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={notificationSettings.messages}
                            onChange={() => handleNotificationChange('messages')}
                          />
                        }
                        label="New Messages"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={notificationSettings.prescriptions}
                            onChange={() => handleNotificationChange('prescriptions')}
                          />
                        }
                        label="Prescription Updates"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={notificationSettings.labResults}
                            onChange={() => handleNotificationChange('labResults')}
                          />
                        }
                        label="Lab Results"
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
              </SettingsPanel>
              
              {/* Privacy & Security Settings */}
              <SettingsPanel
                role="tabpanel"
                hidden={activeTab !== 2}
                id="settings-tabpanel-2"
                aria-labelledby="settings-tab-2"
              >
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  Privacy & Security Settings
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={securitySettings.twoFactorAuth}
                          onChange={() => handleSecurityChange('twoFactorAuth', !securitySettings.twoFactorAuth)}
                        />
                      }
                      label="Two-Factor Authentication"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                      Adds an extra layer of security to your account by requiring a verification code in addition to your password.
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Data Sharing Preferences
                    </Typography>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                      <Select
                        value={securitySettings.dataSharing}
                        onChange={(e) => handleSecurityChange('dataSharing', e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="none">No data sharing</MenuItem>
                        <MenuItem value="minimal">Minimal (required for service)</MenuItem>
                        <MenuItem value="enhanced">Enhanced (for service improvements)</MenuItem>
                        <MenuItem value="research">Include in anonymized research</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Session Timeout (minutes)
                    </Typography>
                    <Box sx={{ px: 2 }}>
                      <Slider
                        value={securitySettings.sessionTimeout}
                        min={5}
                        max={60}
                        step={5}
                        marks={[
                          { value: 5, label: '5' },
                          { value: 30, label: '30' },
                          { value: 60, label: '60' },
                        ]}
                        onChange={(e, value) => handleSecurityChange('sessionTimeout', value)}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Button 
                      variant="outlined" 
                      color="error"
                      sx={{ mt: 2 }}
                    >
                      Change Password
                    </Button>
                  </Grid>
                </Grid>
              </SettingsPanel>
              
              {/* Accessibility Settings */}
              <SettingsPanel
                role="tabpanel"
                hidden={activeTab !== 3}
                id="settings-tabpanel-3"
                aria-labelledby="settings-tab-3"
              >
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  Accessibility Settings
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={accessibilitySettings.screenReader}
                            onChange={() => handleAccessibilityChange('screenReader')}
                          />
                        }
                        label="Screen Reader Compatibility"
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0, mb: 2, ml: 4 }}>
                        Optimizes the interface for screen readers.
                      </Typography>
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={accessibilitySettings.keyboardNavigation}
                            onChange={() => handleAccessibilityChange('keyboardNavigation')}
                          />
                        }
                        label="Enhanced Keyboard Navigation"
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0, mb: 2, ml: 4 }}>
                        Improves navigation using keyboard shortcuts.
                      </Typography>
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={accessibilitySettings.reduceMotion}
                            onChange={() => handleAccessibilityChange('reduceMotion')}
                          />
                        }
                        label="Reduce Motion"
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0, mb: 2, ml: 4 }}>
                        Minimizes animations and transitions.
                      </Typography>
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={accessibilitySettings.autoplayVideos}
                            onChange={() => handleAccessibilityChange('autoplayVideos')}
                          />
                        }
                        label="Autoplay Videos"
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0, mb: 2, ml: 4 }}>
                        Automatically play video content.
                      </Typography>
                    </FormGroup>
                  </Grid>
                </Grid>
              </SettingsPanel>

              {/* Schedule Management */}
              {isPhysician && (
                <SettingsPanel
                  role="tabpanel"
                  hidden={activeTab !== 4}
                  id="settings-tabpanel-schedule"
                  aria-labelledby="settings-tab-schedule"
                >
                  <ScheduleManagement />
                </SettingsPanel>
              )}
              
              {/* User Management */}
              {(isPhysician || isClinicAdmin) && (
                <SettingsPanel
                  role="tabpanel"
                  hidden={activeTab !== (isPhysician ? 6 : (isClinicAdmin ? 5 : 4))}
                  id="settings-tabpanel-5"
                  aria-labelledby="settings-tab-5"
                >
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                    User Management
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          mb: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <GroupIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Clinic Staff
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Create and manage clinic staff accounts. Staff members will receive login credentials via email.
                        </Typography>
                        
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<PersonAddIcon />}
                          onClick={() => setClinicStaffModalOpen(true)}
                          sx={{ mt: 1 }}
                        >
                          Add Clinic Staff
                        </Button>
                      </Paper>
                    </Grid>
                  </Grid>
                </SettingsPanel>
              )}

              {/* Admin Settings */}
              {isPhysician && (
                <SettingsPanel
                  role="tabpanel"
                  hidden={activeTab !== 4}
                  id="settings-tabpanel-4"
                  aria-labelledby="settings-tab-4"
                >
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                    Admin Settings
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          mb: 3,
                          bgcolor: isAdmin ? 'success.light' : 'background.paper',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <SupervisorAccountIcon 
                            sx={{ 
                              mr: 2, 
                              color: isAdmin ? 'success.dark' : 'text.secondary',
                              fontSize: 28
                            }} 
                          />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Admin Status
                          </Typography>
                        </Box>
                        
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {isAdmin 
                            ? "You currently have administrator privileges. You can manage physicians, view system settings, and perform administrative tasks."
                            : "You do not have administrator privileges. Toggle the switch below to enable admin mode."}
                        </Typography>
                        
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isAdmin}
                              onChange={toggleAdminStatus}
                              color="success"
                            />
                          }
                          label={isAdmin ? "Admin Mode Enabled" : "Enable Admin Mode"}
                        />
                      </Paper>
                    </Grid>
                    
                    {isAdmin && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                            Physician Management
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 3,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              mb: 2,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <PersonAddIcon sx={{ mr: 2, color: 'primary.main' }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Physician Registration
                              </Typography>
                            </Box>
                            
                            <FormGroup>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={adminSettings.allowNewPhysicianRegistration}
                                    onChange={() => handleAdminChange('allowNewPhysicianRegistration', !adminSettings.allowNewPhysicianRegistration)}
                                  />
                                }
                                label="Allow New Physician Registration"
                              />
                              
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={adminSettings.requireApprovalForNewPhysicians}
                                    onChange={() => handleAdminChange('requireApprovalForNewPhysicians', !adminSettings.requireApprovalForNewPhysicians)}
                                    disabled={!adminSettings.allowNewPhysicianRegistration}
                                  />
                                }
                                label="Require Admin Approval for New Physicians"
                              />
                            </FormGroup>
                          </Paper>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<PersonAddIcon />}
                            component={Link}
                            to="/admin/physicians"
                            sx={{ mt: 2 }}
                          >
                            Manage Physicians
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </SettingsPanel>
              )}
              
              {/* Save Button */}
              <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider', textAlign: 'right' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveSettings}
                >
                  Save Settings
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Clinic Staff Creation Modal */}
      <UserCreateModal
        open={clinicStaffModalOpen}
        onClose={() => setClinicStaffModalOpen(false)}
        onUserCreated={() => {
          setClinicStaffModalOpen(false);
          toast.success('Clinic staff member created successfully!');
        }}
        role="clinic_staff"
      />
    </SettingsContainer>
  );
};

export default Settings;
